// import AIconButton from '@ahamove/web-components/lib/AIconButton';
import CircularProgress from '@ahamove/web-components/lib/CircularProgress';
import ListItem from '@ahamove/web-components/lib/ListItem';
import ListItemAvatar from '@ahamove/web-components/lib/ListItemAvatar';
import ListItemButton from '@ahamove/web-components/lib/ListItemButton';
import ListItemText from '@ahamove/web-components/lib/ListItemText';
import { useTranslation } from 'next-i18next';
import React, { Fragment } from 'react';
import type { Address } from '@/api/place/types';
import { CurrentLocationIcon, WarningIcon } from '@/lib/icons';
import { homeConfig } from '@/modules/home/homeConfig';
import useCurrentLocation from '@/shared-hooks/useCurrentLocation';

type Props = {
  onSelect: (address: Address) => void;
};

function CurrentLocation({ onSelect }: Props) {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const {
    currentLocation,
    loading: loadingCurrentLocation,
    isError: isCurrentLocationError,
    getCurrentLocation,
  } = useCurrentLocation();

  if (!currentLocation || loadingCurrentLocation) {
    return (
      <ListItem
        sx={{
          bgcolor: 'common.white',
          borderBottomWidth: 5,
          borderBottomStyle: 'solid',
          borderBottomColor: 'grey.50',
        }}
      >
        <ListItemButton
          data-test-id="currentLocationContainer"
          disableRipple
          disabled={loadingCurrentLocation || isCurrentLocationError}
          onClick={isCurrentLocationError ? undefined : getCurrentLocation}
          sx={{
            minHeight: 64,
            bgcolor:
              !loadingCurrentLocation && isCurrentLocationError
                ? 'warning.light'
                : 'common.white',
            pr: '16px !important',
            opacity: loadingCurrentLocation ? '0.38' : '1 !important',
          }}
        >
          <ListItemAvatar sx={{ minWidth: 40 }}>
            {loadingCurrentLocation ? (
              <CircularProgress color="inherit" size={20} />
            ) : isCurrentLocationError ? (
              <WarningIcon style={{ margin: 4 }} />
            ) : (
              <CurrentLocationIcon />
            )}
          </ListItemAvatar>
          {loadingCurrentLocation ? (
            <ListItemText
              primary={t('booking:location.use_current_location')}
              primaryTypographyProps={{
                variant: 'caption',
                color: 'info.main',
              }}
            />
          ) : isCurrentLocationError ? (
            <ListItemText
              primary={t('booking:location.detect_current_location_error')}
              primaryTypographyProps={{
                variant: 'caption',
                color: 'warning.main',
                fontWeight: '500 !important',
              }}
            />
          ) : (
            <Fragment />
          )}
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <ListItem
      // divider
      // secondaryAction={
      //   <AIconButton
      //     color="tertiary"
      //     size="small"
      //     rounded
      //     onClick={(e) => {
      //       e.stopPropagation();
      //       getCurrentLocation();
      //     }}
      //   >
      //     <LocationIcon />
      //   </AIconButton>
      // }
      sx={{
        bgcolor: 'common.white',
        borderBottomWidth: 5,
        borderBottomStyle: 'solid',
        borderBottomColor: 'grey.50',
      }}
    >
      <ListItemButton disableRipple onClick={() => onSelect(currentLocation)}>
        <ListItemAvatar sx={{ minWidth: 40 }}>
          <CurrentLocationIcon />
        </ListItemAvatar>
        <ListItemText
          primary={t('booking:location.use_current_location')}
          primaryTypographyProps={{
            variant: 'h6',
            color: 'info.main',
          }}
          secondary={currentLocation.address}
          secondaryTypographyProps={{
            variant: 'caption',
            color: 'grey.700',
            fontWeight: 500,
            noWrap: true,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

export default React.memo(CurrentLocation);
